<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

defineProps<{ mobileOpen: boolean }>()
const emit = defineEmits<{ closeMobile: [] }>()

const { t } = useI18n()
const route = useRoute()

interface NavItem {
  routeName: string
  labelKey: string
  activePrefix: string
  exact: boolean
  iconPaths: string[]
}

interface NavSection {
  key: string
  labelKey: string
  items: NavItem[]
}

const CALENDAR_ICON: string[] = [
  'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
]

const GRID_ICON: string[] = [
  'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 018.25 20.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
]

const BUILDING_ICON: string[] = [
  'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z',
]

const USERS_ICON: string[] = [
  'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
]

const INBOX_ICON: string[] = [
  'M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z',
]

const COG_ICON: string[] = [
  'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.216.456a1.125 1.125 0 01-1.37-.49l-1.296-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

const CURRENCY_ICON: string[] = [
  'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
]

const CLOCK_ICON: string[] = [
  'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
]

const DESKTOP_ICON: string[] = [
  'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25',
]

const CHART_ICON: string[] = [
  'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
]

const BANKNOTES_ICON: string[] = [
  'M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75z',
  'M12 15.75a3 3 0 100-6 3 3 0 000 6z',
  'M6 8.25h.008v.008H6V8.25zm12 7.5h.008v.008H18v-7.508z',
]

const DOCUMENT_ICON: string[] = [
  'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
]

const navSections: NavSection[] = [
  {
    key: 'overview',
    labelKey: 'admin.nav.section_overview',
    items: [
      {
        routeName: 'admin.dashboard',
        labelKey: 'admin.nav.dashboard',
        activePrefix: 'admin.dashboard',
        exact: true,
        iconPaths: GRID_ICON,
      },
    ],
  },
  {
    key: 'tenants',
    labelKey: 'admin.nav.section_tenants',
    items: [
      {
        routeName: 'admin.organisations.index',
        labelKey: 'admin.nav.organisations',
        activePrefix: 'admin.organisations',
        exact: false,
        iconPaths: BUILDING_ICON,
      },
      {
        routeName: 'admin.users.index',
        labelKey: 'admin.nav.users',
        activePrefix: 'admin.users',
        exact: false,
        iconPaths: USERS_ICON,
      },
    ],
  },
  {
    key: 'finance',
    labelKey: 'admin.nav.section_finance',
    items: [
      {
        routeName: 'admin.finance.transactions.index',
        labelKey: 'admin.nav.transactions',
        activePrefix: 'admin.finance',
        exact: false,
        iconPaths: BANKNOTES_ICON,
      },
      {
        routeName: 'admin.invoicing.index',
        labelKey: 'admin.nav.invoicing',
        activePrefix: 'admin.invoicing',
        exact: false,
        iconPaths: DOCUMENT_ICON,
      },
    ],
  },
  {
    key: 'support',
    labelKey: 'admin.nav.section_support',
    items: [
      {
        routeName: 'admin.support.index',
        labelKey: 'admin.nav.support',
        activePrefix: 'admin.support',
        exact: false,
        iconPaths: INBOX_ICON,
      },
    ],
  },
  {
    key: 'platform',
    labelKey: 'admin.nav.section_platform',
    items: [
      {
        routeName: 'admin.settings.index',
        labelKey: 'admin.nav.settings',
        activePrefix: 'admin.settings',
        // Was `exact: false` — with `admin.settings.history` also under the
        // `admin.settings` prefix, that highlighted this item on the history
        // page too. Exact match keeps exactly one nav item active at a time.
        exact: true,
        iconPaths: COG_ICON,
      },
      {
        routeName: 'admin.pricing.index',
        labelKey: 'admin.nav.pricing',
        activePrefix: 'admin.pricing',
        exact: false,
        iconPaths: CURRENCY_ICON,
      },
      {
        routeName: 'admin.settings.history',
        labelKey: 'admin.nav.history',
        activePrefix: 'admin.settings.history',
        exact: true,
        iconPaths: CLOCK_ICON,
      },
    ],
  },
  {
    key: 'system',
    labelKey: 'admin.nav.section_system',
    items: [
      {
        routeName: 'admin.system.sessions.index',
        labelKey: 'admin.nav.sessions',
        activePrefix: 'admin.system.sessions',
        exact: false,
        iconPaths: DESKTOP_ICON,
      },
      {
        routeName: 'admin.system.scheduled-tasks.index',
        labelKey: 'admin.nav.scheduled_tasks',
        activePrefix: 'admin.system.scheduled-tasks',
        exact: false,
        iconPaths: CALENDAR_ICON,
      },
      {
        routeName: 'admin.system.analytics.index',
        labelKey: 'admin.nav.analytics',
        activePrefix: 'admin.system.analytics',
        exact: false,
        iconPaths: CHART_ICON,
      },
    ],
  },
]

function isItemActive(item: NavItem): boolean {
  if (!route.name) return false
  if (item.exact) return route.name === item.routeName
  return String(route.name).startsWith(item.activePrefix)
}
</script>

<template>
  <!-- Desktop sidebar — always visible on lg+ -->
  <aside
    class="hidden lg:flex flex-col shrink-0 w-64 bg-slate-900 border-r border-slate-700"
    aria-label="Main navigation"
  >
    <div class="flex items-center px-6 py-5 border-b border-slate-700 shrink-0">
      <span class="text-white font-bold text-lg tracking-tight">Startline Admin</span>
    </div>

    <nav class="flex-1 overflow-y-auto px-3 py-4 space-y-6">
      <div v-for="section in navSections" :key="section.key">
        <p class="px-3 mb-1 text-xs font-semibold uppercase tracking-widest text-slate-500">
          {{ t(section.labelKey) }}
        </p>
        <RouterLink
          v-for="item in section.items"
          :key="item.routeName"
          :to="{ name: item.routeName }"
          class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          :class="isItemActive(item) ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'"
        >
          <svg
            class="w-5 h-5 shrink-0 transition-colors"
            :class="isItemActive(item) ? 'text-emerald-400' : ''"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <path
              v-for="d in item.iconPaths"
              :key="d"
              stroke-linecap="round"
              stroke-linejoin="round"
              :d="d"
            />
          </svg>
          {{ t(item.labelKey) }}
        </RouterLink>
      </div>
    </nav>
  </aside>

  <!-- Mobile sidebar overlay — Teleport'd to body to escape overflow-hidden -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      leave-active-class="transition-opacity duration-150 ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div v-if="mobileOpen" class="fixed inset-0 z-40 flex lg:hidden">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm"
          aria-hidden="true"
          @click="emit('closeMobile')"
        />

        <!-- Panel -->
        <aside
          class="relative z-50 flex flex-col w-64 bg-slate-900 border-r border-slate-700 h-full shadow-2xl"
          aria-label="Main navigation"
        >
          <div class="flex items-center justify-between px-6 py-5 border-b border-slate-700 shrink-0">
            <span class="text-white font-bold text-lg tracking-tight">Startline Admin</span>
            <button
              type="button"
              class="text-slate-400 hover:text-white transition-colors"
              :aria-label="t('admin.common.close')"
              @click="emit('closeMobile')"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav class="flex-1 overflow-y-auto px-3 py-4 space-y-6">
            <div v-for="section in navSections" :key="section.key">
              <p class="px-3 mb-1 text-xs font-semibold uppercase tracking-widest text-slate-500">
                {{ t(section.labelKey) }}
              </p>
              <RouterLink
                v-for="item in section.items"
                :key="item.routeName"
                :to="{ name: item.routeName }"
                class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                :class="isItemActive(item) ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'"
                @click="emit('closeMobile')"
              >
                <svg
                  class="w-5 h-5 shrink-0 transition-colors"
                  :class="isItemActive(item) ? 'text-emerald-400' : ''"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="1.5"
                  aria-hidden="true"
                >
                  <path
                    v-for="d in item.iconPaths"
                    :key="d"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    :d="d"
                  />
                </svg>
                {{ t(item.labelKey) }}
              </RouterLink>
            </div>
          </nav>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>
